"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { Sparkles } from "lucide-react";

export default function Matrix() {
    const pathname = usePathname();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const streamsRef = useRef<Stream[]>([]); // Persist streams between enable/disable
    const [enabled, setEnabled] = useState(true);
    const hidden = pathname?.startsWith("/admin") || pathname?.startsWith("/raffle-overlay");

    type Stream = {
        x: number;
        y: number;
        speed: number; // px per second
        word: string;
        shine: number; // 0 to 1, intensity of shine effect
        shineDecay: number; // how fast shine fades
    };

    useEffect(() => {
        if (!enabled || hidden) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        let width = (canvas.width = window.innerWidth);
        let height = (canvas.height = window.innerHeight);

        const words = [
            "SOKOLOCO",
            "CAN'T TAKE SH*T SERIOUS",
            "BOOOOOOOM",
            "NOT TOO F**KING SHABBY",
            "AT LEAST SOMETHING",
            "SKOLKA N*?",
            "STARTED FROM ZERO",
            "LAST DEPO I PROMISE",
            "WHO'S YOUR DADDY?",
            "LAST ONE",
        ];

        const columnWidth = 14; // slightly wider spacing improves legibility
        const columns = Math.floor(width / columnWidth);

        if (streamsRef.current.length === 0) {
            streamsRef.current = Array.from({ length: columns }, (_, i) => ({
                x: i * columnWidth,
                y: Math.random() * height,
                speed: 60 + Math.random() * 40,
                word: words[Math.floor(Math.random() * words.length)],
                shine: 0,
                shineDecay: 0,
            }));
        }

        let lastTime = performance.now();
        let animationId: number;

        function draw(now: number) {
            if (!ctx) return;

            const delta = Math.min((now - lastTime) / 1000, 0.05); // limit delta for smoother motion
            lastTime = now;

            // Slight transparency creates smoother trailing effect
            ctx.fillStyle = "rgba(12, 12, 12)";
            ctx.fillRect(0, 0, width, height);

            ctx.font = "16px monospace";

            const streams = streamsRef.current;

            // Randomly trigger shine effect (small chance each frame)
            if (Math.random() < 0.02) {
                const randomStream = streams[Math.floor(Math.random() * streams.length)];
                if (randomStream.shine === 0) {
                    randomStream.shine = 1;
                    randomStream.shineDecay = 1.5 + Math.random() * 1.5; // decay speed varies
                }
            }

            for (let i = 0; i < streams.length; i++) {
                const stream = streams[i];

                // Calculate color based on shine intensity
                const baseColor = 0x21; // base green component
                const shineBoost = Math.floor(stream.shine * 200); // add up to 200 to green
                const green = Math.min(255, baseColor + shineBoost);
                ctx.fillStyle = `rgb(${baseColor}, ${green}, ${baseColor})`;

                ctx.fillText(stream.word, stream.x, stream.y);
                stream.y += stream.speed * delta;

                // Decay shine effect
                if (stream.shine > 0) {
                    stream.shine = Math.max(0, stream.shine - stream.shineDecay * delta);
                }

                if (stream.y > height + 100) {
                    stream.y = Math.random() * -200;
                    stream.speed = 50 + Math.random() * 40;
                    stream.word = words[Math.floor(Math.random() * words.length)];
                    stream.shine = 0;
                }
            }

            animationId = requestAnimationFrame(draw);
        }

        animationId = requestAnimationFrame(draw);

        const handleResize = () => {
            width = canvas.width = window.innerWidth;
            height = canvas.height = window.innerHeight;

            // Recalculate columns and redistribute streams
            const newColumns = Math.floor(width / columnWidth);
            streamsRef.current = Array.from({ length: newColumns }, (_, i) => ({
                x: i * columnWidth,
                y: Math.random() * height,
                speed: 60 + Math.random() * 40,
                word: words[Math.floor(Math.random() * words.length)],
                shine: 0,
                shineDecay: 0,
            }));
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                lastTime = performance.now();
            }
        };

        window.addEventListener("resize", handleResize);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            window.removeEventListener("resize", handleResize);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            cancelAnimationFrame(animationId);
        };
    }, [enabled, hidden]);

    if (hidden) return null;

    return (
        <>
            {enabled && (
                <canvas
                    ref={canvasRef}
                    className="fixed top-0 left-0 w-full h-full -z-10"
                    style={{ background: "rgba(12, 12, 12, 0.99)" }}
                />
            )}

            <button
                onClick={() => setEnabled((prev) => !prev)}
                style={{ left: 'calc(var(--sidebar-rail-w, 4rem) / 2)', transform: 'translateX(-50%)' }}
                className={`fixed bottom-4 z-[55] flex items-center justify-center w-12 h-12 rounded-full border shadow-lg transition-all duration-200 ${enabled
                    ? "bg-black text-green-400 border-green-500/40 hover:bg-green-400/10 hover:border-green-400/70 hover:shadow-emerald-500/20"
                    : "bg-green-400 text-black border-green-400 hover:bg-green-300 hover:shadow-emerald-500/30"
                }`}
            >
                <Sparkles className="w-6 h-6" />
            </button>
        </>
    );
}